import { useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, Baby, Shield, Globe } from 'lucide-react';

interface AgeVerificationProps {
  dateOfBirth: string;
  setDateOfBirth: (value: string) => void;
  parentEmail: string;
  setParentEmail: (value: string) => void;
  parentalConsentGiven: boolean;
  setParentalConsentGiven: (value: boolean) => void;
  showParentalConsent: boolean;
  countryCode: string;
  setCountryCode: (value: string) => void;
}

// Age of consent by country (matches database function)
const getAgeOfConsent = (country: string): number => {
  const ukEu = ['GB','UK','AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR','HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE'];
  const coppa = ['US','CA','AU','NZ'];
  
  if (ukEu.includes(country)) return 16;
  if (coppa.includes(country)) return 13;
  if (country === 'KR') return 14;
  return 16; // Default
};

export const AgeVerification: React.FC<AgeVerificationProps> = ({
  dateOfBirth,
  setDateOfBirth,
  parentEmail,
  setParentEmail,
  parentalConsentGiven,
  setParentalConsentGiven,
  showParentalConsent,
  countryCode,
  setCountryCode,
}) => {
  const [age, setAge] = useState<number | null>(null);
  const ageOfConsent = getAgeOfConsent(countryCode);
  const needsParentalConsent = age !== null && age >= ageOfConsent && age < 18;

  const calculateAge = (dob: string) => {
    if (!dob) return null;
    
    const today = new Date();
    const birthDate = new Date(dob);
    let calculatedAge = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      calculatedAge--;
    }
    
    return calculatedAge;
  };

  const handleDateChange = (value: string) => {
    setDateOfBirth(value);
    const calculatedAge = calculateAge(value);
    setAge(calculatedAge);
  };

  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="country" className="flex items-center gap-2">
          <Globe className="h-4 w-4" />
          Country *
        </Label>
        <Select value={countryCode} onValueChange={setCountryCode} required>
          <SelectTrigger className="mt-1">
            <SelectValue placeholder="Select your country" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="GB">United Kingdom</SelectItem>
            <SelectItem value="US">United States</SelectItem>
            <SelectItem value="CA">Canada</SelectItem>
            <SelectItem value="AU">Australia</SelectItem>
            <SelectItem value="IE">Ireland</SelectItem>
            <SelectItem value="FR">France</SelectItem>
            <SelectItem value="DE">Germany</SelectItem>
            <SelectItem value="ES">Spain</SelectItem>
            <SelectItem value="IT">Italy</SelectItem>
            <SelectItem value="NL">Netherlands</SelectItem>
            <SelectItem value="OTHER">Other</SelectItem>
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground mt-1">
          Required for age verification compliance (minimum age: {ageOfConsent})
        </p>
      </div>

      <div>
        <Label htmlFor="dateOfBirth" className="flex items-center gap-2">
          <Baby className="h-4 w-4" />
          Date of Birth *
        </Label>
        <Input
          id="dateOfBirth"
          type="date"
          value={dateOfBirth}
          onChange={(e) => handleDateChange(e.target.value)}
          required
          className="mt-1"
        />
        {age !== null && age < ageOfConsent && (
          <Alert className="mt-2" variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              You must be at least {ageOfConsent} years old to use Therappi<sup className="text-[0.6em]">®</sup> in {countryCode === 'GB' ? 'the UK' : 'your country'}. 
              {age >= 13 && (
                <span className="block mt-2">
                  However, you may be eligible for <strong>Therappi<sup className="text-[0.6em]">®</sup> for Schools</strong>, which is designed for younger users with institutional support.
                </span>
              )}
            </AlertDescription>
          </Alert>
        )}
      </div>

      {showParentalConsent && needsParentalConsent && (
        <div className="space-y-3 p-4 border border-warning/30 rounded-lg bg-warning/5">
          <Alert>
            <Shield className="h-4 w-4" />
            <AlertDescription>
              <strong>Parental Consent Required:</strong> Users aged {ageOfConsent}-17 need parental or guardian consent to use Therappi<sup className="text-[0.6em]">®</sup>.
              {countryCode === 'GB' && age !== null && age >= 16 && (
                <span className="block mt-2 text-sm">
                  Note: In the UK, users 16+ can consent to digital services under GDPR, but we still require parental awareness for users under 18.
                </span>
              )}
            </AlertDescription>
          </Alert>

          <div>
            <Label htmlFor="parentEmail" className="flex items-center gap-2">
              Parent/Guardian Email *
            </Label>
            <Input
              id="parentEmail"
              type="email"
              value={parentEmail}
              onChange={(e) => setParentEmail(e.target.value)}
              placeholder="parent@example.com"
              required
              className="mt-1"
            />
            <p className="text-xs text-muted-foreground mt-1">
              We'll send a consent verification email to your parent/guardian.
            </p>
          </div>

          <div className="flex items-start space-x-2">
            <Checkbox
              id="parentalConsent"
              checked={parentalConsentGiven}
              onCheckedChange={(checked) => setParentalConsentGiven(checked === true)}
            />
            <label
              htmlFor="parentalConsent"
              className="text-sm leading-tight cursor-pointer"
            >
              My parent/guardian has reviewed Therappi<sup className="text-[0.6em]">®</sup>'s Terms of Service and Privacy Policy and consents to my use of the service. *
            </label>
          </div>

          <Alert variant="default" className="text-xs">
            <AlertDescription>
              <strong>Important for Parents/Guardians:</strong>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>You'll receive an email to verify this consent</li>
                <li>Your child's data will be protected with age-appropriate restrictions</li>
                <li>You can request data deletion at any time by contacting support@therappi.app</li>
                <li>Features like community posts and certain content are restricted for minors</li>
              </ul>
            </AlertDescription>
          </Alert>
        </div>
      )}

      {age !== null && age >= 18 && (
        <Alert>
          <Shield className="h-4 w-4" />
          <AlertDescription className="text-xs">
            As an adult user, you have full access to all Therappi<sup className="text-[0.6em]">®</sup> features.
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};
